import * as React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * Softens a route change. Two things made navigation feel abrupt: the new page
 * snapped in with no lead-in, and it inherited the previous page's scroll offset,
 * so following a link from halfway down a row landed the viewer in the middle of
 * the next page.
 *
 * Both are handled here, in the shell, so every pilet gets the same treatment
 * without knowing about it:
 *
 * - **Enter animation.** The wrapper is keyed on the pathname, so React remounts
 *   it on a route change and the CSS animation replays (see `.PageTransition` in
 *   `style.scss`). The key is the pathname *only* — not the search string — so a
 *   page that keeps state in the query (the search pilet's `?q=`) does not
 *   re-animate and remount on every keystroke.
 * - **Scroll reset**, on a forward navigation only. A `POP` (browser back/forward)
 *   is left alone so the browser's own scroll restoration can put the viewer back
 *   where they were.
 *
 * No exit animation: the outgoing tree is gone by the time we render, and keeping
 * a copy of it alive would mean two mounted pilet pages — and two sets of their
 * data fetches — for the sake of a cross-fade.
 */
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const history = useHistory()

  React.useEffect(() => {
    if (history.action === 'POP') return
    // `auto`, not `smooth`: the page is fading in at the same moment, and a
    // smooth scroll racing that animation is exactly the roughness this fixes.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  return (
    <div key={location.pathname} className='PageTransition'>
      {children}
    </div>
  )
}

export default PageTransition
